import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleApproval = async (reviewId, currentApproved) => {
    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    try {
      await fetch(`${backendUrl}/api/admin/reviews/${reviewId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ approved: !currentApproved })
      });

      loadReviews();
    } catch (error) {
      alert('Failed to update review');
    }
  };

  const toggleFeatured = async (reviewId, currentFeatured) => {
    const token = localStorage.getItem('admin_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    try {
      await fetch(`${backendUrl}/api/admin/reviews/${reviewId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ featured: !currentFeatured })
      });

      loadReviews();
    } catch (error) {
      alert('Failed to update review');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i key={i} className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}></i>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="https://images.squarespace-cdn.com/content/v1/6329980709c417765c18ef9c/1663928993488-9PHELX1GHR1MGT4SNOLP/minchio+brothers+logo.png?format=100w" alt="Logo" className="h-10 w-10" />
              <h1 className="text-xl font-bold text-gray-900">Reviews</h1>
            </div>
            <button onClick={handleLogout} className="text-red-600 hover:text-red-700">
              <i className="ri-logout-box-line"></i> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="flex">
        <div className="w-64 bg-white h-screen shadow-sm">
          <nav className="p-4 space-y-2">
            <Link to="/admin/dashboard" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-dashboard-line text-xl"></i><span>Dashboard</span>
            </Link>
            <Link to="/admin/bookings" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-calendar-check-line text-xl"></i><span>Bookings</span>
            </Link>
            <Link to="/admin/contacts" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-message-3-line text-xl"></i><span>Contacts</span>
            </Link>
            <Link to="/admin/reviews" className="flex items-center space-x-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-lg font-medium">
              <i className="ri-star-line text-xl"></i><span>Reviews</span>
            </Link>
            <Link to="/admin/settings" className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg">
              <i className="ri-settings-3-line text-xl"></i><span>Settings</span>
            </Link>
          </nav>
        </div>

        <div className="flex-1 p-8">
          <div className="bg-white rounded-xl shadow-sm border">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : reviews.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No reviews yet</div>
            ) : (
              <div className="divide-y">
                {reviews.map((review) => (
                  <div key={review.id} className="p-6 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{review.name}</h3>
                          {review.featured && (
                            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">Featured</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {renderStars(review.rating)}
                          <span className="text-sm text-gray-500">({review.rating}/5)</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-3">{review.review_text}</p>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleApproval(review.id, review.approved)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          review.approved 
                            ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {review.approved ? 'Unapprove' : 'Approve'}
                      </button>
                      <button
                        onClick={() => toggleFeatured(review.id, review.featured)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          review.featured 
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        {review.featured ? 'Unfeature' : 'Feature'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
